import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';
import { Close, Flag } from '@mui/icons-material';
import { useReportReasons } from '../../hooks/useCommunity';
import { useReportPost, useReportComment } from '../../hooks/useCommunityMutations';
import type { CommunityPost, CommunityComment, ReportReason } from '../../types/community';

const reportSchema = z.object({
  reason: z.string().min(1, 'Please select a reason'),
  details: z
    .string()
    .max(500, 'Details must be under 500 characters')
    .optional()
    .transform((val) => val?.trim() || undefined),
});

type ReportFormData = z.infer<typeof reportSchema>;

interface ReportDialogProps {
  open: boolean;
  onClose: () => void;
  target: CommunityPost | CommunityComment | null;
  type: 'post' | 'comment';
}

export default function ReportDialog({
  open,
  onClose,
  target,
  type,
}: ReportDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: reasonsData, isLoading: loadingReasons } = useReportReasons();
  const reportPost = useReportPost();
  const reportComment = useReportComment();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReportFormData>({
    resolver: zodResolver(reportSchema),
    defaultValues: {
      reason: '',
      details: '',
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      reset({ reason: '', details: '' });
      setError(null);
      setSuccess(false);
    }
  }, [open, reset]);

  const onSubmit = async (data: ReportFormData) => {
    if (!target) return;
    setError(null);

    try {
      if (type === 'post') {
        await reportPost.mutateAsync({
          id: target._id,
          data: {
            reason: data.reason as ReportReason,
            details: data.details,
          },
        });
      } else {
        await reportComment.mutateAsync({
          id: target._id,
          data: {
            reason: data.reason as ReportReason,
            details: data.details,
          },
        });
      }
      setSuccess(true);
      // Auto-close after showing success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to submit report');
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    setSuccess(false);
    onClose();
  };

  const reasons = reasonsData?.data?.reasons || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#DC2626',
              }}
            >
              <Flag />
            </Box>
            <Typography variant="h6">
              Report {type === 'post' ? 'Post' : 'Comment'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Thank you for your report. Our team will review it shortly.
            </Alert>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Help us understand why you're reporting this content. Your report
                will be reviewed by our moderation team.
              </Typography>

              {/* Reason Selection */}
              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <FormControl error={!!errors.reason} fullWidth sx={{ mb: 3 }}>
                    <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
                      Why are you reporting this?
                    </FormLabel>
                    {loadingReasons ? (
                      <Box display="flex" alignItems="center" gap={1} py={2}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          Loading reasons...
                        </Typography>
                      </Box>
                    ) : (
                      <RadioGroup {...field}>
                        {reasons.map((reason) => (
                          <FormControlLabel
                            key={reason.value}
                            value={reason.value}
                            control={<Radio />}
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {reason.label}
                                </Typography>
                                {reason.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {reason.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{
                              alignItems: 'flex-start',
                              mb: 1,
                              '& .MuiRadio-root': { pt: 0.5 },
                            }}
                          />
                        ))}
                      </RadioGroup>
                    )}
                    {errors.reason && (
                      <FormHelperText>{errors.reason.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {/* Additional Details */}
              <Controller
                name="details"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Additional Details (Optional)"
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Provide any additional context that might help us understand the issue..."
                    error={!!errors.details}
                    helperText={errors.details?.message}
                    inputProps={{ maxLength: 500 }}
                  />
                )}
              />

              <Box
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  p: 2,
                  mt: 3,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  False reports may result in action being taken against your account.
                  Please only report content that genuinely violates our community guidelines.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>

        {!success && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || loadingReasons}
              sx={{
                backgroundColor: '#DC2626',
                '&:hover': {
                  backgroundColor: '#DC2626',
                  opacity: 0.9,
                },
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                  Submitting...
                </>
              ) : (
                'Submit Report'
              )}
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
}
